// features/buyer/products/components/ProductDetailsPage/ProductDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useCart } from '../../../../../contexts/CartContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import { 
  ShoppingCart, 
  Heart, 
  Share2, 
  Truck, 
  Shield, 
  RotateCcw,
  Check,
  AlertCircle,
  Plus,
  Minus,
  Package,
  Star,
  Building,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  IndianRupee,
  Calendar,
  Award,
  Factory,
  Ruler,
  Scale,
  Box,
  Tag,
  Palette,
  FileText,
  X,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  ExternalLink,
  Percent,
  ShieldCheck,
  Clock,
  Gauge,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const ProductDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [quantityError, setQuantityError] = useState('');

  // Format warranty for display with professional styling
  const formatWarranty = (warranty) => {
    if (!warranty || warranty === 'No Warranty') {
      return {
        text: 'No Warranty',
        icon: Shield,
        color: 'text-gray-600',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-200',
        description: 'This product comes without any warranty'
      };
    }
    
    if (warranty.includes('Lifetime')) {
      return {
        text: 'Lifetime Warranty',
        icon: Award,
        color: 'text-purple-600',
        bgColor: 'bg-purple-100',
        borderColor: 'border-purple-200',
        description: 'Lifetime warranty coverage on this product'
      };
    }
    
    if (warranty.includes('Manufacturer')) {
      return {
        text: warranty,
        icon: ShieldCheck,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-200',
        description: 'Manufacturer warranty on parts and labor'
      };
    }
    
    if (warranty.includes('Seller')) {
      return {
        text: warranty,
        icon: Shield,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200',
        description: 'Seller-backed warranty for this product'
      };
    }
    
    // Default for warranty periods (1 Year, 2 Years, etc.)
    return {
      text: warranty,
      icon: Clock,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      borderColor: 'border-orange-200',
      description: `${warranty} warranty period`
    };
  };

  // Validate quantity against MOQ and stock
  const validateQuantity = (value) => {
    const moq = product?.moq || 1;
    const stock = product?.stock || product?.quantity || Infinity;
    
    if (value < moq) {
      setQuantityError(`Minimum order quantity is ${moq} ${product?.unit || 'PCS'}`);
      return false;
    }
    
    if (value > stock) {
      setQuantityError(`Only ${stock} ${product?.unit || 'PCS'} available in stock`);
      return false;
    }
    
    setQuantityError('');
    return true;
  };

  // Handle quantity change with MOQ validation
  const handleQuantityChange = (newQuantity) => {
    const moq = product?.moq || 1;
    const stock = product?.stock || product?.quantity || Infinity;
    
    // Ensure quantity doesn't go below MOQ
    if (newQuantity < moq) {
      setQuantity(moq);
      validateQuantity(moq);
    } 
    // Ensure quantity doesn't exceed stock
    else if (newQuantity > stock) {
      setQuantity(stock);
      validateQuantity(stock);
    } 
    else {
      setQuantity(newQuantity);
      validateQuantity(newQuantity);
    }
  };

  // Handle increment with MOQ steps
  const incrementQuantity = () => {
    const moq = product?.moq || 1;
    const stock = product?.stock || product?.quantity || Infinity;
    const newQuantity = quantity + moq; // Increment by MOQ
    
    if (newQuantity <= stock) {
      setQuantity(newQuantity);
      validateQuantity(newQuantity);
    } else {
      toast.error(`Cannot exceed available stock of ${stock} ${product?.unit || 'PCS'}`);
    }
  };

  // Handle decrement with MOQ steps
  const decrementQuantity = () => {
    const moq = product?.moq || 1;
    const newQuantity = quantity - moq; // Decrement by MOQ
    
    if (newQuantity >= moq) {
      setQuantity(newQuantity);
      validateQuantity(newQuantity);
    } else {
      toast.error(`Minimum order quantity is ${moq} ${product?.unit || 'PCS'}`);
    }
  };

  // Fetch product details
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productRef = doc(db, 'products', id);
        const productSnap = await getDoc(productRef);
        
        if (productSnap.exists()) {
          const productData = productSnap.data();
          console.log('Product data:', productData); // Debug log
          
          setProduct({
            id: productSnap.id,
            ...productData
          });
          
          // Set quantity to MOQ
          const moq = productData.moq || 1;
          setQuantity(moq);
          
          // Set default selected color if available
          const colors = productData.colors || productData.productColors || '';
          if (colors && colors.trim() !== '') {
            const colorArray = colors.split(/[,+&|/]/).map(c => c.trim()).filter(c => c);
            if (colorArray.length > 0) {
              setSelectedColor(colorArray[0]);
            }
          }
        } else {
          toast.error('Product not found');
          navigate('/buyer/products');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, navigate]);

  // Parse colors from string to array
  const parseColors = (colorsString) => {
    if (!colorsString || colorsString.trim() === '') return [];
    return colorsString.split(/[,+&|/]/).map(color => color.trim()).filter(color => color);
  };

  // Get available colors
  const availableColors = parseColors(product?.colors || product?.productColors || '');

  // Get all images
  const getAllImages = () => {
    const images = [];
    if (product?.images && Array.isArray(product.images)) {
      product.images.forEach(img => {
        if (img?.data) images.push(img.data);
        else if (typeof img === 'string') images.push(img);
        else if (img?.url) images.push(img.url);
      });
    }
    if (product?.image) images.push(product.image);
    if (product?.productImage) images.push(product.productImage);
    return images.length > 0 ? images : ['/placeholder-image.jpg'];
  };

  const images = getAllImages();

  // Handle add to cart with MOQ validation
  const handleAddToCart = () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      navigate('/login');
      return;
    }

    if (!product) return;

    // Final validation before adding to cart
    if (!validateQuantity(quantity)) {
      toast.error(quantityError);
      return;
    }

    const cartItem = {
      productId: product.id,
      productName: product.name || product.productName,
      productImage: images[selectedImageIndex] || images[0] || '/placeholder-image.jpg',
      unitPrice: product.price || product.sellingPrice || 0,
      quantity: quantity,
      selectedColor: selectedColor,
      productColors: product.colors || product.productColors || '',
      sellerId: product.sellerId,
      sellerName: product.sellerName || product.businessName,
      maxQuantity: product.stock || product.quantity || 10,
      moq: product.moq || 1,
      unit: product.unit || 'PCS',
      warranty: product.warranty || 'No Warranty' // Include warranty in cart item
    };

    addToCart(cartItem);
    setAddedToCart(true);
    
    toast.success(`Added ${quantity} ${product.unit || 'PCS'} to cart!`);
    
    // Reset added state after 2 seconds
    setTimeout(() => setAddedToCart(false), 2000);
  };

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Navigate images
  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-300 rounded w-48 mb-6"></div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-300 rounded-lg"></div>
              <div className="space-y-4">
                <div className="h-8 bg-gray-300 rounded w-3/4"></div>
                <div className="h-6 bg-gray-300 rounded w-1/2"></div>
                <div className="h-24 bg-gray-300 rounded"></div>
                <div className="h-12 bg-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const warrantyInfo = formatWarranty(product.warranty);
  const WarrantyIcon = warrantyInfo.icon;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="flex items-center text-sm text-gray-600 mb-6">
          <button onClick={() => navigate('/')} className="hover:text-orange-600">Home</button>
          <ChevronRight className="h-4 w-4 mx-2" />
          <button onClick={() => navigate('/buyer/products')} className="hover:text-orange-600">Products</button>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-gray-900 font-medium line-clamp-1">{product.name || product.productName}</span>
        </nav>

        {/* Main Product Section */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-8">
            {/* Product Images Section */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border border-gray-200 group">
                <img
                  src={images[selectedImageIndex]}
                  alt={product.name || product.productName}
                  className="w-full h-full object-contain cursor-zoom-in"
                  onClick={() => setShowImageModal(true)}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/placeholder-image.jpg';
                  }}
                />
                
                {/* Image Navigation Arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <ChevronRightIcon className="h-5 w-5" />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {images.length > 1 && (
                  <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
                    {selectedImageIndex + 1} / {images.length}
                  </div>
                )}

                {/* Warranty Badge on Image */}
                {product.warranty && product.warranty !== 'No Warranty' && (
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 ${warrantyInfo.bgColor} ${warrantyInfo.color} text-xs rounded-full flex items-center border ${warrantyInfo.borderColor}`}>
                      <WarrantyIcon className="w-3 h-3 mr-1" />
                      {warrantyInfo.text}
                    </span>
                  </div>
                )}
              </div>

              {/* Thumbnail Images */}
              {images.length > 1 && (
                <div className="grid grid-cols-5 gap-2">
                  {images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                        selectedImageIndex === index 
                          ? 'border-orange-500 scale-105' 
                          : 'border-gray-200 hover:border-orange-300'
                      }`}
                    >
                      <img 
                        src={img} 
                        alt={`${product.name} thumbnail ${index + 1}`} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = '/placeholder-image.jpg';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info Section */}
            <div className="space-y-6">
              {/* Product Status Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium flex items-center">
                  <Check className="h-3 w-3 mr-1" />
                  APPROVED
                </span>
                {/* {product.stock > 0 ? (
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    In Stock
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-xs font-medium">
                    Out of Stock
                  </span>
                )}
                 */}
                {/* Warranty Badge in status section */}
                {product.warranty && product.warranty !== 'No Warranty' && (
                  <span className={`px-3 py-1 ${warrantyInfo.bgColor} ${warrantyInfo.color} rounded-full text-xs font-medium flex items-center border ${warrantyInfo.borderColor}`}>
                    <WarrantyIcon className="w-3 h-3 mr-1" />
                    {warrantyInfo.text}
                  </span>
                )}
              </div>

              {/* Title and Price */}
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">
                  {product.name || product.productName}
                </h1>
                
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="text-3xl font-bold text-orange-600 flex items-center">
                    {formatINR(product.price || product.sellingPrice || 0)}
                  </div>
                  {product.originalPrice && (
                    <div className="text-lg text-gray-400 line-through flex items-center">
                      <IndianRupee className="h-4 w-4" />
                      {formatINR(product.originalPrice)}
                    </div>
                  )}
                  {product.discount && (
                    <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                      {product.discount}% OFF
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-1">FOB Price / {product.unit || 'Piece'}</p>
              </div>

              {/* Rating */}
              {product.rating ? (
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-4 w-4 ${
                          i < Math.floor(product.rating) 
                            ? 'text-yellow-400 fill-yellow-400' 
                            : 'text-gray-300'
                        }`} 
                      />
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">
                    ({product.reviewCount || product.reviews || 0} reviews)
                  </span>
                </div>
              ) : (
                <div className="text-sm text-gray-500">No ratings yet</div>
              )}

              {/* Key Specifications */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Gauge className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">MOQ</p>
                    <p className="text-sm font-medium">{product.moq || 1} {product.unit || 'PCS'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Factory className="h-4 w-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Business Type</p>
                    <p className="text-sm font-medium">{product.businessType || 'Manufacturer'}</p>
                  </div>
                </div>
                {product.material && (
                  <div className="flex items-center space-x-2">
                    <Scale className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Material</p>
                      <p className="text-sm font-medium">{product.material}</p>
                    </div>
                  </div>
                )}
                {product.standard && (
                  <div className="flex items-center space-x-2">
                    <Award className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Standard</p>
                      <p className="text-sm font-medium">{product.standard}</p>
                    </div>
                  </div>
                )}
                {/* Warranty in Specifications */}
                {product.warranty && (
                  <div className="flex items-center space-x-2">
                    <WarrantyIcon className={`h-4 w-4 ${warrantyInfo.color}`} />
                    <div>
                      <p className="text-xs text-gray-500">Warranty</p>
                      <p className="text-sm font-medium">{product.warranty}</p>
                    </div>
                  </div>
                )}
                {/* GST in Specifications */}
                {product.gst && (
                  <div className="flex items-center space-x-2">
                    <Percent className="h-4 w-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">GST percentage</p>
                      <p className="text-sm font-medium">{product.gst}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-orange-600" />
                  Product Description
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {product.description || product.productDescription || 'No description available.'}
                </p>
              </div>

              {/* Color Selection */}
              {availableColors.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <Palette className="h-4 w-4 mr-2 text-orange-600" />
                    Select Color
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {availableColors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(color)}
                        className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                          selectedColor === color
                            ? 'bg-orange-600 text-white border-orange-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-orange-500'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                  {product.colors && (
                    <p className="text-xs text-gray-500 mt-1">
                      All available colors: {product.colors}
                    </p>
                  )}
                </div>
              )}

              {/* Quantity with MOQ Validation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity ({product.unit || 'PCS'})
                </label>
                <div className="flex flex-col space-y-2">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={decrementQuantity}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={quantity <= (product.moq || 1)}
                      title={`Minimum ${product.moq || 1} ${product.unit || 'PCS'}`}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="w-20 text-center font-medium text-lg">{quantity}</span>
                    <button
                      onClick={incrementQuantity}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={quantity >= (product.stock || product.quantity || Infinity)}
                      title={`Maximum ${product.stock || product.quantity || 'available'} ${product.unit || 'PCS'}`}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600 ml-2">
                      {product.stock || product.quantity || 'N/A'} available
                    </span>
                  </div>
                  
                  {/* MOQ Information */}
                  <div className="flex items-start space-x-2 text-sm">
                    <Info className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-gray-700">
                        <span className="font-medium">Minimum Order Quantity:</span> {product.moq || 1} {product.unit || 'PCS'}
                      </p>
                      <p className="text-gray-600 text-xs mt-1">
                        Quantity can be adjusted in multiples of {product.moq || 1} {product.unit || 'PCS'}
                      </p>
                    </div>
                  </div>

                  {/* Quantity Error Message */}
                  {quantityError && (
                    <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 p-2 rounded-lg">
                      <AlertCircle className="h-4 w-4 flex-shrink-0" />
                      <span>{quantityError}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Add to Cart Button Only */}
              <div className="flex flex-col gap-4 pt-4">
                <button
                  onClick={handleAddToCart}
                  disabled={product.stock === 0 || !!quantityError}
                  className={`w-full flex items-center justify-center space-x-2 px-6 py-4 rounded-lg font-medium transition-all transform hover:scale-105 ${
                    addedToCart
                      ? 'bg-green-600 text-white'
                      : product.stock === 0 || quantityError
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-orange-600 text-white hover:bg-orange-700'
                  }`}
                >
                  {addedToCart ? (
                    <>
                      <Check className="h-5 w-5" />
                      <span>Added to Cart!</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCart className="h-5 w-5" />
                      <span>
                        {product.stock === 0 
                          ? 'Out of Stock' 
                          : quantityError 
                          ? 'Invalid Quantity' 
                          : `Add ${quantity} ${product.unit || 'PCS'} to Cart`}
                      </span>
                    </>
                  )}
                </button>
              </div>

              {/* Additional Actions */}
              <div className="flex items-center justify-center gap-6 pt-4 border-t border-gray-200">
                <button className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors">
                  <Heart className="h-5 w-5" />
                  <span className="text-sm">Add to Wishlist</span>
                </button>
                <button className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors">
                  <Share2 className="h-5 w-5" />
                  <span className="text-sm">Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Product Specifications */}
        {product.specifications && product.specifications.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Tag className="h-6 w-6 mr-2 text-orange-600" />
              Product Specifications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.specifications.map((spec, index) => (
                <div key={index} className="flex items-start space-x-2 p-3 bg-gray-50 rounded-lg">
                  <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{spec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Warranty Details Section - NEW */}
        {product.warranty && product.warranty !== 'No Warranty' && (
          <div className={`bg-white rounded-xl shadow-lg p-6 mb-8 border-l-4 ${warrantyInfo.borderColor}`}>
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <WarrantyIcon className={`h-6 w-6 mr-2 ${warrantyInfo.color}`} />
              Warranty & Guarantee Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${warrantyInfo.bgColor}`}>
                    <WarrantyIcon className={`h-5 w-5 ${warrantyInfo.color}`} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Warranty Period</h3>
                    <p className="text-gray-700">{product.warranty}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Shield className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Coverage</h3>
                    <p className="text-gray-700">
                      {product.warranty.includes('Lifetime') 
                        ? 'Lifetime coverage against manufacturing defects' 
                        : product.warranty.includes('Manufacturer')
                        ? 'Manufacturer warranty on parts and labor'
                        : product.warranty.includes('Seller')
                        ? 'Seller-backed warranty for this product'
                        : `${product.warranty} warranty against defects`}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Calendar className="h-5 w-5 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Valid From</h3>
                    <p className="text-gray-700">Date of Purchase</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg bg-gray-100">
                    <Check className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Terms & Conditions</h3>
                    <p className="text-gray-700">Standard warranty terms apply. Please check with seller for specific conditions.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
            <Truck className="h-10 w-10 text-orange-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Free Shipping</h3>
              <p className="text-sm text-gray-600">On orders above ₹5000</p>
            </div>
          </div> */}
          <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
            <Shield className="h-10 w-10 text-orange-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Secure Payment</h3>
              <p className="text-sm text-gray-600">100% secure transactions</p>
            </div>
          </div>
          {/* <div className="bg-white rounded-xl shadow-lg p-6 flex items-center space-x-4">
            <RotateCcw className="h-10 w-10 text-orange-600" />
            <div>
              <h3 className="font-semibold text-gray-900">Easy Returns</h3>
              <p className="text-sm text-gray-600">7 days return policy</p>
            </div>
          </div> */}
        </div>
      </div>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 z-10 p-2 text-white hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>

          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={images[selectedImageIndex]}
              alt={product.name}
              className="max-h-full max-w-full object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-image.jpg';
              }}
            />
            
            {images.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronLeft className="h-6 w-6 text-white" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/40 p-3 rounded-full"
                >
                  <ChevronRightIcon className="h-6 w-6 text-white" />
                </button>
              </>
            )}

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`w-2 h-2 rounded-full ${
                    idx === selectedImageIndex ? 'bg-orange-500' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Product Info in Modal - Updated with Warranty and MOQ */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            <h3 className="text-white font-semibold text-lg mb-1">{product.name}</h3>
            <div className="flex items-center text-white/90 flex-wrap gap-2">
              <div className="flex items-center">
                <IndianRupee className="h-4 w-4 mr-1" />
                <span className="font-bold text-orange-300">{formatINR(product.price)}</span>
              </div>
              <span className="mx-2">•</span>
              <div className="flex items-center">
                <Gauge className="h-4 w-4 mr-1" />
                <span className="text-sm">MOQ: {product.moq || 1} {product.unit || 'PCS'}</span>
              </div>
              {product.warranty && product.warranty !== 'No Warranty' && (
                <>
                  <span className="mx-2">•</span>
                  <div className={`flex items-center ${warrantyInfo.color}`}>
                    <WarrantyIcon className="h-4 w-4 mr-1" />
                    <span className="text-sm">{warrantyInfo.text}</span>
                  </div>
                </>
              )}
              {product.gst && (
                <>
                  <span className="mx-2">•</span>
                  <div className="flex items-center">
                    <Percent className="h-3 w-3 mr-1" />
                    <span className="text-sm">GST: {product.gst}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailsPage;